import { ref, computed } from 'vue'
import type { Ref, ComputedRef } from 'vue'
import { useIntervalFn, createSharedComposable } from '@vueuse/core'
import { whenEppoIsReady, useEppo } from '@/composables/eppo'
import { useReady } from '@/composables/ready'
import { useLogger } from '@booksy-com/vue-tools'
import { useCore } from '@/composables/core'

import { FEATURE_FLAGS } from '@/lib/consts.js'

const logger = useLogger('maintenance-mode')

export const MAINTENANCE_MODE_TYPE = {
  DISABLED: 'disabled',
  INCIDENT: 'incident',
  SCHEDULED: 'scheduled',
} as const

export type MaintenanceModeType = typeof MAINTENANCE_MODE_TYPE[keyof typeof MAINTENANCE_MODE_TYPE]

const MAINTENANCE_MODE_CHECK_INTERVAL = 30000 // 30s

type StoreStateWithApp = {
  state: { app: { fingerprint: string } }
}

export type UseMaintenanceModeReturn = {
  isMaintenanceMode: ComputedRef<boolean>
  maintenanceModeType: ComputedRef<MaintenanceModeType>
  startCheckingMaintenanceMode: () => Promise<void>
  MAINTENANCE_MODE_TYPE: typeof MAINTENANCE_MODE_TYPE
}

export const useMaintenanceMode = createSharedComposable((): UseMaintenanceModeReturn => {
  const { store } = useCore()
  const isCheckingMaintenanceMode: Ref<boolean> = ref(false)
  const isMaintenanceMode: Ref<boolean> = ref(false)
  const maintananceModeType: Ref<MaintenanceModeType> = ref(MAINTENANCE_MODE_TYPE.DISABLED)

  const startCheckingMaintenanceMode = async (): Promise<void> => {
    if (isCheckingMaintenanceMode.value) return

    isCheckingMaintenanceMode.value = true

    return whenEppoIsReady.then(() => {
      useIntervalFn(() => {
        const flagState = useEppo().ff(FEATURE_FLAGS.Maintenance_Mode, null, {
          fingerprint: (store as StoreStateWithApp).state.app.fingerprint,
        }) as MaintenanceModeType | null
        if (isMaintenanceMode.value && flagState === MAINTENANCE_MODE_TYPE.DISABLED) {
          window.location.reload()
          return
        }

        if (flagState === MAINTENANCE_MODE_TYPE.INCIDENT || flagState === MAINTENANCE_MODE_TYPE.SCHEDULED) {
          logger.info(`set maintenance mode ON - type: ${flagState}`)
          isMaintenanceMode.value = true
          maintananceModeType.value = flagState
          useReady().setReady(false)
        }
      }, MAINTENANCE_MODE_CHECK_INTERVAL, { immediateCallback: true })
    }) as Promise<void>
  }

  return {
    isMaintenanceMode: computed(() => isMaintenanceMode.value),
    maintenanceModeType: computed(() => maintananceModeType.value),
    startCheckingMaintenanceMode,
    MAINTENANCE_MODE_TYPE,
  }
})
